import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { AuthModal } from "../components/AuthModal";
import { PaywallModal } from "../components/PaywallModal";

const VisitorAuthContext = createContext(null);

export const VisitorAuthProvider = ({ children }) => {
  const [visitor, setVisitor] = useState(null); // null = checking, false = anon, object = signed in
  const [favourites, setFavourites] = useState([]);
  const [authOpen, setAuthOpen] = useState(false);
  const [access, setAccess] = useState(null); // {is_member, remaining:{curios,quizzes}, limits}
  const [paywall, setPaywall] = useState(null); // {reason, title, message} | null

  const isMember = !!(access && access.is_member);

  const refreshAccess = useCallback(async () => {
    try {
      const { data } = await api.get("/club/access");
      setAccess(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  const applyUser = (user, token) => {
    if (token) localStorage.setItem("cv_visitor_token", token);
    setVisitor(user);
    setFavourites(user.favourites || []);
  };

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("cv_visitor_token");
    if (!token) {
      setVisitor(false);
      return;
    }
    try {
      const { data } = await api.get("/visitor/me");
      setVisitor(data);
      setFavourites(data.favourites || []);
    } catch (e) {
      localStorage.removeItem("cv_visitor_token");
      setVisitor(false);
    }
  }, []);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      if (sessionId) {
        api
          .post("/visitor/google-session", {}, { headers: { "X-Session-ID": sessionId } })
          .then(({ data }) => applyUser(data.user, data.token))
          .catch(() => refresh());
        return;
      }
    }
    refresh();
  }, [refresh]);

  const registerEmail = async (email, password, name) => {
    const { data } = await api.post("/visitor/register", { email, password, name });
    applyUser(data.user, data.token);
    return data.user;
  };

  const loginEmail = async (email, password) => {
    const { data } = await api.post("/visitor/login", { email, password });
    applyUser(data.user, data.token);
    return data.user;
  };

  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = () => {
    localStorage.removeItem("cv_visitor_token");
    setVisitor(false);
    setFavourites([]);
  };

  const isFavourite = (slug) => favourites.includes(slug);

  const toggleFavourite = async (slug) => {
    if (!visitor) {
      setAuthOpen(true);
      return false;
    }
    if (favourites.includes(slug)) {
      const { data } = await api.delete(`/visitor/favourites/${slug}`);
      setFavourites(data.favourites);
    } else {
      const { data } = await api.post(`/visitor/favourites/${slug}`);
      setFavourites(data.favourites);
    }
    return true;
  };

  const openAuth = () => setAuthOpen(true);
  const closeAuth = () => setAuthOpen(false);
  const openPaywall = (info) => setPaywall(info || { title: "CurioVault Club", message: "Join the Club to unlock this." });
  const closePaywall = () => setPaywall(null);

  useEffect(() => { refreshAccess(); }, [visitor, refreshAccess]);

  // Try to use one unit of the free allowance (or pass instantly for members).
  // Returns true if allowed; otherwise opens the paywall and returns false.
  const consume = useCallback(async (type, ref = "") => {
    try {
      const { data } = await api.post("/club/consume", { type, ref });
      if (data.allowed) {
        if (!data.member) refreshAccess();
        return true;
      }
      openPaywall({
        reason: "limit",
        title: type === "curio" ? "Daily curio limit reached" : "Daily quiz limit reached",
        message: type === "curio"
          ? `Free readers get ${data.limit} curios a day. Join the Club for unlimited access to all 100 curios and the full Pub Quiz.`
          : `Free players get ${data.limit} ${data.limit === 1 ? "quiz" : "quizzes"} a day plus the Daily Three. Join the Club for unlimited quizzes, every category, host mode and the archive.`,
      });
      return false;
    } catch {
      return true; // never hard-block on a network hiccup
    }
  }, [refreshAccess]);

  // Gate a members-only feature (archive, host mode, full categories).
  const requireMember = useCallback((info) => {
    if (isMember) return true;
    openPaywall(info || {
      reason: "members_only",
      title: "Club members only",
      message: "This is part of CurioVault Club — unlimited quizzes, every category, host mode and the full archive.",
    });
    return false;
  }, [isMember]);

  const checkout = useCallback(async (lookupKey) => {
    if (!visitor) { setAuthOpen(true); return; }
    const { data } = await api.post("/payments/checkout", {
      lookup_key: lookupKey,
      origin_url: window.location.origin,
    });
    window.location.href = data.checkout_url;
  }, [visitor]);

  const openPortal = useCallback(async () => {
    const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
    window.location.href = data.url;
  }, []);

  const value = useMemo(
    () => ({
      visitor,
      favourites,
      authOpen,
      openAuth,
      closeAuth,
      registerEmail,
      loginEmail,
      loginGoogle,
      logout,
      isFavourite,
      toggleFavourite,
      refresh,
      access,
      isMember,
      refreshAccess,
      consume,
      requireMember,
      checkout,
      openPortal,
      paywall,
      openPaywall,
      closePaywall,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visitor, favourites, authOpen, access, isMember, paywall]
  );

  return (
    <VisitorAuthContext.Provider value={value}>
      {children}
      <AuthModal />
      <PaywallModal />
    </VisitorAuthContext.Provider>
  );
};

export const useVisitor = () => useContext(VisitorAuthContext);
